import SvgIcon from '@mui/material/SvgIcon';
import React from 'react';
import { ITranslationKeys, Device, IUGCReviewItemProps } from '@interfaces';
import { useToggleContentScroll } from '../../../../hooks/useToggleContentScroll';
import CabinTypeIcon from '../../../../../public/static/icons/cabin.svg';
import { theme } from '../../../../style/global-style';
import { getDetectedDevice } from '../../../../utils/device-detection';
import {
    eventTracker,
    getLandingPageActions,
    getLandingPageCategory,
} from '../../../../utils/EventTracker';
import { ILabelName } from '../../../../utils/normalizeLabels';
import { __ } from '../../../../utils/translation-service';
import { LabelBox } from '../../../atoms/label-box/label-box';
import { StyledTextLabelListItem } from '../../../molecules/offer-board-card/offer-board-card.style';
import { StyledArrowIcon } from '../../../sections/highlights/highlights.style';
import { UGCReviewComment } from './ugc-review-comment';
import {
    StyledAgeRange,
    StyledAtmosphere,
    StyledCabinComment,
    StyledConstellation,
    StyledName,
    StyledOverlay,
    StyledRating,
    StyledReviewBody,
    StyledReviewDesktopHeaderContent,
    StyledReviewHeader,
    StyledReviewMobileHeaderContent,
    StyledShowMoreBtn,
    StyledTextOpacity,
    StyledUGCArticle,
} from './ugc-review-item.style';
import { formatDateDDMMMMYYYY } from '../../../../../src/utils/date';

export const UgcReviewItem = (props: IUGCReviewItemProps) => {
    const {
        overallRating,
        name,
        constellation,
        arrival,
        cruise,
        atmosphere,
        positivePointsComment,
        negativePointsComment,
        cabinType,
        cabinComment,
        ageRange,
    } = props.review;
    const device: Device = getDetectedDevice();
    const ugcWrapperRef = React.createRef<HTMLDivElement>();
    const { isCollapsed, toggleCollapse, maxHeightReviewContent } =
        useToggleContentScroll(ugcWrapperRef);
    const date = arrival ? formatDateDDMMMMYYYY(arrival) : '';
    const UGCREVIEWITEM_INITIALHEIGHT = device === 'tablet' ? 120 : 150;

    const atmosIgnored = ['discreet', 'inspiring', 'stressful', 'impersonal'];
    const filteredAtmosphere =
        atmosphere && atmosphere.filter((atmosItem) => !atmosIgnored.includes(atmosItem));
    const formatConstellation = constellation?.replace(/[^\w\s]/gi, '');
    return (
        <StyledUGCArticle>
            <StyledReviewHeader>
                <StyledOverlay />
                <StyledReviewMobileHeaderContent>
                    <div>
                        <StyledName>
                            {name}
                            <StyledAgeRange>{ageRange && `(${ageRange})`}</StyledAgeRange>
                        </StyledName>
                        {!!formatConstellation && (
                            <StyledConstellation>
                                {__(`constellation${formatConstellation}Label` as ITranslationKeys)}
                            </StyledConstellation>
                        )}
                    </div>
                    <div>
                        <StyledRating>
                            <span>
                                {overallRating && overallRating.toFixed
                                    ? overallRating.toFixed(1)
                                    : ''}
                            </span>
                            <span>{` / 10`}</span>
                        </StyledRating>
                    </div>
                </StyledReviewMobileHeaderContent>
                <StyledReviewDesktopHeaderContent>
                    <StyledRating>
                        <span>
                            {overallRating && overallRating.toFixed ? overallRating.toFixed(1) : ''}
                        </span>
                        <span>{` / 10`}</span>
                    </StyledRating>
                    <StyledName>{name}</StyledName>
                    <StyledAgeRange>{ageRange && `(${ageRange})`}</StyledAgeRange>
                    <StyledConstellation>
                        {__(`constellation${formatConstellation}Label` as ITranslationKeys)}
                    </StyledConstellation>
                </StyledReviewDesktopHeaderContent>
            </StyledReviewHeader>
            <StyledReviewBody
                isCollapsed={isCollapsed}
                ref={ugcWrapperRef}
                scrollContentHeight={maxHeightReviewContent}
            >
                <h3>{cruise}</h3>
                {filteredAtmosphere && (
                    <StyledAtmosphere>
                        {filteredAtmosphere.length > 0 &&
                            filteredAtmosphere.map((atmosLabel, key) => {
                                const labelWithAtmosphere = `atmosphere${atmosLabel.toLowerCase()}`;
                                return (
                                    <StyledTextLabelListItem key={key}>
                                        <LabelBox
                                            type={labelWithAtmosphere as ILabelName}
                                            label={__(labelWithAtmosphere as ITranslationKeys)}
                                        />
                                    </StyledTextLabelListItem>
                                );
                            })}
                    </StyledAtmosphere>
                )}
                <UGCReviewComment comment={positivePointsComment} iconType="positive" />
                <UGCReviewComment comment={negativePointsComment} iconType="negative" />
                {cabinComment ? (
                    <StyledCabinComment>
                        <SvgIcon style={{ fontSize: 15, color: theme.shades.darkGrey }}>
                            <CabinTypeIcon />
                        </SvgIcon>{' '}
                        <div>
                            <h4>{cabinType}</h4>
                            <UGCReviewComment comment={cabinComment} />
                        </div>
                    </StyledCabinComment>
                ) : null}
                <StyledTextOpacity />
            </StyledReviewBody>
            {maxHeightReviewContent && maxHeightReviewContent > UGCREVIEWITEM_INITIALHEIGHT ? (
                <StyledShowMoreBtn
                    onClick={() => {
                        // @TODO: set category to wharever is the parent of this thingy
                        eventTracker.track({
                            event: 'click',
                            category: getLandingPageCategory('ugc'),
                            action: getLandingPageActions('ugc'),
                            label: 'see more',
                        });
                        return toggleCollapse();
                    }}
                    aria-expanded={!isCollapsed}
                    data-testid="toggle-content-btn"
                >
                    {isCollapsed ? `mehr anzeigen` : `weniger anzeigen`}{' '}
                    <StyledArrowIcon isCollapsed={isCollapsed} />
                </StyledShowMoreBtn>
            ) : null}
        </StyledUGCArticle>
    );
};
export default UgcReviewItem;
